import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { bounce, cubic, elastic, quadratic, quartic } from '@juliendargelos/easings';
import { Container, useTick } from '@pixi/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { isConfigFlagEnabled } from '../../../config';
import { removePlayerAnimation } from '../../../redux/actions/animationsActions';
import { selectActionSeatTimeProperties } from '../../../redux/reducers/pokerReducer';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { getRemainingTimeRatio } from '../../../utils/animationUtils';
import { assertUnreachable } from '../../../utils/tsUtils';
import { isTestEnvironment } from '../../../utils/utils';
function sitXFn(ratio, dropX) {
    return dropX - dropX * quadratic.out(ratio);
}
function sitYFn(ratio, dropY) {
    return -dropY + dropY * bounce.out(ratio);
}
function bombBounceFn(ratio, strength) {
    const unscaledPosition = ratio < 0.2 ? cubic.out(ratio / 0.2) : 1 - elastic.out(((ratio - 0.2) * 5) / 4);
    return unscaledPosition * strength;
}
function betActionYFn(ratio, totalY) {
    return (1 - quartic.in(ratio)) * totalY;
}
function chatActionYFn(ratio, totalY) {
    return (1 - quartic.in(ratio)) * totalY;
}
function playerAnimationsEqualityFn(animations1, animations2) {
    return shallowEqual(animations1.map(({ id }) => id), animations2.map(({ id }) => id));
}
function isAnimationActive(playerAnimations, actionSeatTimeProperties, isConnectionLost) {
    if (isConnectionLost) {
        // stop all player animations if connection is lost
        return false;
    }
    return !!playerAnimations.length || !!actionSeatTimeProperties;
}
function getHeadNodDeltaMultiplier(remainingTimeRatio) {
    const baseMultiplier = 0.03;
    const deltaMultiplierRange = 0.1;
    return baseMultiplier + quartic.in(1 - remainingTimeRatio) * deltaMultiplierRange;
}
/**
 * In the last few seconds of a player's turn, add an increasingly nervous shake animation.
 */
function getHeadShakeCoords(remainingTimeRatio) {
    const remainingTimeStartRatio = 0.3;
    const maxPlayerShakeDeltaPx = 5;
    if (isConfigFlagEnabled('expiringTimePlayerShakeAnimation') &&
        !isConfigFlagEnabled('expiringTimeTableShakeAnimation') &&
        remainingTimeRatio <= remainingTimeStartRatio) {
        // only allow player shaking from one function across the codebase
        const playerShakeElapsedRatio = 1 - remainingTimeRatio / remainingTimeStartRatio;
        const maxShakeDelta = quadratic.in(playerShakeElapsedRatio) * maxPlayerShakeDeltaPx;
        const deltaX = Math.random() * maxShakeDelta - maxShakeDelta / 2;
        const deltaY = Math.random() * maxShakeDelta - maxShakeDelta / 2;
        return [deltaX, deltaY];
    }
}
export const PlayerAnimationContainer = ({ seatIndex, children }) => {
    const dispatch = useAppDispatch();
    const playerAnimations = useAppSelector((state) => state.animations.playerAnimations[seatIndex] || [], playerAnimationsEqualityFn);
    const actionSeatTimeProperties = useAppSelector((state) => selectActionSeatTimeProperties(state, seatIndex), shallowEqual);
    const activePlayerAnimationRadius = useAppSelector((state) => state.gameDimensions.roughAvatarSizePx !== undefined ? state.gameDimensions.roughAvatarSizePx / 40 : undefined);
    const isConnectionLost = useAppSelector((state) => state.session.isConnectionLost);
    const [isAnimating, setIsAnimating] = useState(isAnimationActive(playerAnimations, actionSeatTimeProperties, isConnectionLost));
    const containerRef = useRef(null);
    const radians = useRef(0);
    const animate = useCallback((delta) => {
        if (!isAnimationActive(playerAnimations, actionSeatTimeProperties, isConnectionLost)) {
            if (containerRef.current) {
                // player may no longer be the action seat after being the action seat, so reset animation position
                containerRef.current.x = 0;
                containerRef.current.y = 0;
            }
            setIsAnimating(false);
            return;
        }
        if (!containerRef.current) {
            return;
        }
        const nowMs = performance.now();
        let totalX = 0;
        let totalY = 0;
        playerAnimations.forEach((playerAnimation) => {
            const ratioDone = Math.min(1, (nowMs - playerAnimation.startTimestampMs) / playerAnimation.durationMs);
            if (ratioDone < 1) {
                switch (playerAnimation.type) {
                    case 'sit': {
                        totalX += sitXFn(ratioDone, playerAnimation.dropX);
                        totalY += sitYFn(ratioDone, playerAnimation.dropY);
                        break;
                    }
                    case 'bomb-bounce':
                    case 'throw-recoil': {
                        const scaleFactor = 0.02;
                        const baseCoordDisplacement = scaleFactor * bombBounceFn(ratioDone, playerAnimation.strength);
                        totalX += playerAnimation.velocityX * baseCoordDisplacement;
                        totalY += playerAnimation.velocityY * baseCoordDisplacement;
                        break;
                    }
                    case 'bet-action': {
                        totalY += betActionYFn(ratioDone, playerAnimation.totalY);
                        break;
                    }
                    case 'chat-action': {
                        totalY += chatActionYFn(ratioDone, playerAnimation.totalY);
                        break;
                    }
                    default: {
                        assertUnreachable(playerAnimation);
                    }
                }
            }
            else {
                dispatch(removePlayerAnimation(seatIndex, playerAnimation.id));
            }
        });
        // add "nodding" animation to action player:
        if (actionSeatTimeProperties && activePlayerAnimationRadius) {
            // TODO: Write a test around this animation being active or inactive
            const remainingTimeRatio = getRemainingTimeRatio(actionSeatTimeProperties.startTimestampMs, actionSeatTimeProperties.totalMs, nowMs);
            radians.current += delta * getHeadNodDeltaMultiplier(remainingTimeRatio);
            totalY += activePlayerAnimationRadius * Math.sin(radians.current);
            const headShakeCoords = getHeadShakeCoords(remainingTimeRatio);
            if (headShakeCoords) {
                const [headShakeX, headShakeY] = headShakeCoords;
                totalX += headShakeX;
                totalY += headShakeY;
            }
        }
        containerRef.current.x = totalX;
        containerRef.current.y = totalY;
    }, [isConnectionLost, playerAnimations, actionSeatTimeProperties, activePlayerAnimationRadius, dispatch, seatIndex]);
    useEffect(() => {
        if (actionSeatTimeProperties) {
            // when action seat is changed to this one, reset the "phase" of the animation
            radians.current = 0;
        }
    }, [actionSeatTimeProperties]);
    useEffect(() => {
        const newIsAnimating = isAnimationActive(playerAnimations, actionSeatTimeProperties, isConnectionLost);
        if (newIsAnimating !== isAnimating) {
            setIsAnimating(newIsAnimating);
        }
    }, [actionSeatTimeProperties, isAnimating, playerAnimations, isConnectionLost]);
    useTick(animate, isAnimating);
    const pixiData = isTestEnvironment()
        ? {
            type: 'player-animation',
            seatIndex,
            animationTypes: playerAnimations.map((playerAnimation) => playerAnimation.type)
        }
        : null;
    return (_jsx(Container, { ref: containerRef, x: 0, y: 0, "pixi-data": pixiData, children: children }));
};
